import { useTranslation } from "react-i18next";

import { cn } from "src/core/lib/utils";

type PaginationProps = {
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  pageSize: number;
  onPageSizeChange: (pageSize: number) => void;
  totalCount: number;
};

export const Pagination = ({
  currentPage,
  totalPages,
  onPageChange,
  pageSize,
  onPageSizeChange,
  totalCount,
}: PaginationProps) => {
  const { t } = useTranslation();

  const getPages = () => {
    // currentPage et les indexes sont 0‑based
    if (totalPages <= 7) {
      return Array.from({ length: totalPages }, (_, i) => i);
    }

    const pages: (number | "ellipsis-left" | "ellipsis-right")[] = [];

    const firstPage = 0;
    const lastPage = totalPages - 1;
    const start = Math.max(1, currentPage - 1);
    const end = Math.min(totalPages - 2, currentPage + 1);

    pages.push(firstPage);

    if (start > 1) {
      pages.push("ellipsis-left");
    }

    for (let i = start; i <= end; i++) {
      pages.push(i);
    }

    if (end < totalPages - 2) {
      pages.push("ellipsis-right");
    }

    pages.push(lastPage);

    return pages;
  };

  return (
    <div className="flex items-center py-20 gap-x-40 justify-center">
      {/* Div vide pour équilibrer l'espace */}
      <div className="font-light">{t("pagination.total", { totalCount })}</div>

      {/* Div centrée avec les numéros de page */}
      <div className="flex items-center gap-x-5">
        {getPages().map((page, index) =>
          typeof page === "number" ? (
            <button
              key={page}
              onClick={() => onPageChange(page)}
              className={cn("pagination-item", currentPage === page ? "current-page" : "page-item")}
            >
              {page + 1}
            </button>
          ) : (
            <span
              key={`${page}-${index}`}
              className="text-lg text-gray-medium font-medium select-none"
            >
              ...
            </span>
          )
        )}
      </div>

      {/* Div à la fin avec le sélecteur de taille de page */}
      <div className="flex items-center gap-x-5">
        <p className="font-light">{t("pagination.linesPerPage")}</p>
        <select
          value={pageSize}
          onChange={(e) => onPageSizeChange(Number(e.target.value))}
          className="h-20 w-50 bg-background px-2 focus:border-none hover:cursor-pointer font-light"
        >
          {[10, 20, 30, 40, 50].map((size) => (
            <option key={size} value={size}>
              {size}
            </option>
          ))}
        </select>
      </div>
    </div>
  );
};
